/* SWE Rewards runs only inside its dedicated live or read-only preview section. */
(() => {
  'use strict';
  if (customElements.get('swe-member-rewards')) return;

  const EXPECTED_RULES = Object.freeze({ version: '2026-10-19-v3', pointsPerRewardDollar: 100, rateBps: 500, minRedemptionCents: 500, maxRedemptionCents: 2500, minimumPurchaseMultiplier: 2, holdDays: 30, codeExpiryDays: 90 });
  const REWARD_COSTS = Object.freeze([500, 1000, 2500]);
  const GIFT_AVAILABILITY = Object.freeze(['available', 'out_of_stock', 'unavailable', 'not_enabled']);
  const BOOT_HOOKS = Object.freeze({ id: 'boot-hooks', kind: 'product', definitionVersion: 'boot-hooks-v1', title: '10-inch boot hooks', pointsCost: 1000, variantId: 'gid://shopify/ProductVariant/54457004654883', quantity: 1, minimumPurchaseCents: 0, shippingIncluded: false });
  const BOOT_HOOKS_PRODUCT_PATH = '/products/boot-hooks-boot-pullers-wood-handles?variant=54457004654883';
  const CODE_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{2,127}$/;
  const messages = {
    LOGIN_REQUIRED: 'Please sign in again to see your rewards.',
    INVALID_CSRF: 'Your secure session needs refreshing. Choose Try again before continuing.',
    INSUFFICIENT_BALANCE: 'Your points have changed. Refresh your account before choosing a reward.',
    ACTIVE_REWARD: 'You already have a reward in progress. Refresh to see its current status.',
    IDEMPOTENCY_CONFLICT: 'This request needs a status check. Refresh your rewards before trying again.',
    PROGRAM_PAUSED: 'Rewards are temporarily paused. Your recorded rewards are kept. Please try again later.',
    NOT_CONFIGURED: 'Rewards are not available yet. Please check back later.',
    INVALID_INPUT: 'Please check your details and try again.',
    INVALID_REDEMPTION_OPTION: 'Choose one of the offered points rewards. Refresh your account if the choices have changed.',
    REWARD_UNAVAILABLE: 'This reward is unavailable right now. Your points remain in your account. Refresh to check again.',
    PRODUCT_REWARD_UNAVAILABLE: 'The boot-hooks reward is unavailable right now. Your points remain in your account. Refresh to check again.',
    PRODUCT_REWARD_OUT_OF_STOCK: 'Temporarily out of stock. Refresh your rewards to check the boot-hooks reward. Your recorded points are kept.',
    PRODUCT_REWARD_NOT_CONFIGURED: 'The boot-hooks reward is not available yet. Your recorded points are kept.',
    CART_NOT_CONFIRMED: "We couldn't confirm the cart update. Check your cart or choose Apply again. We will check the cart before adding a set.",
    CART_CURRENCY_UNSUPPORTED: 'This reward requires a cart in US dollars. Change your shopping currency to USD, then refresh your rewards. No reward code was applied.',
    PRODUCT_PRICE_CHANGED: 'Your boot-hooks reward needs a price check. Refresh your rewards before applying it. Check your cart before checkout. Any boot hooks there may be at their normal price.',
    REWARD_CHANGED_DURING_APPLY: 'Your reward changed while we checked it. Review the updated account before applying a code.',
    CODE_EXPIRED_DURING_APPLY: 'Your code expired before it could be applied. Boot hooks may be in your cart at their normal price. Check your cart before checkout. Your points stay reserved pending verification.',
    RULES_CHANGED: 'The program details are being updated. Please try again later.',
    INVALID_STATE: "We couldn't verify your rewards. Please try again.",
    NETWORK_ERROR: "We couldn't confirm the request. Choose Try again to check your rewards before continuing.",
    READ_ONLY_PREVIEW: 'This is a read-only account preview. Earning, redemption, and submissions are disabled.',
    EXPIRED_REWARD: 'This code has expired. Its points stay reserved until we check whether it was used. Refresh to check its status.',
  };

  class MemberRewards extends HTMLElement {
    isReadOnlyPreview() {
      return this.dataset.enabled !== 'true' && this.dataset.previewAccountEnabled === 'true';
    }

    productRewardEnabled() {
      return this.dataset.enabled === 'true' && this.dataset.productRewardEnabled === 'true';
    }

    bonusEnabled(kind, state = this.state) {
      const setting = kind === 'review' ? 'reviewBonusEnabled' : kind === 'content' ? 'contentBonusEnabled' : null;
      return Boolean(setting && this.dataset.enabled === 'true' && this.dataset[setting] === 'true' && state?.capabilities?.[`${kind}Submissions`] === true);
    }

    renderBonusAvailability(state) {
      for (const kind of ['review', 'content']) {
        const enabled = this.bonusEnabled(kind, state);
        const status = this.query(`[data-${kind}-offer-status]`);
        const availability = this.query(`[data-${kind}-offer-availability]`);
        if (status) status.textContent = enabled ? kind === 'review' ? 'After verification' : 'After approval' : 'Planned bonus';
        if (availability) availability.textContent = enabled ? 'See your account for available submission options.' : kind === 'review' ? 'This bonus is not open yet. Reviews do not currently earn points.' : 'This bonus is not open yet. Photos and videos do not currently earn points.';
      }
    }

    static parseRewardCost(value) {
      const clean = String(value).trim();
      if (!/^[1-9][0-9]{0,8}$/.test(clean)) return null;
      const points = Number(clean);
      return Number.isSafeInteger(points) ? points : null;
    }

    static validGiftDefinition(value, active = false) {
      if (!value || (active ? value.rewardId : value.id) !== BOOT_HOOKS.id) return false;
      return Object.entries(BOOT_HOOKS).every(([key, expected]) => key === 'id' || value[key] === expected);
    }

    static productApplyUrl(reward) {
      try {
        if (typeof reward.applyUrl !== 'string' || typeof reward.code !== 'string' || !CODE_PATTERN.test(reward.code)) throw new Error();
        const url = new URL(reward.applyUrl, window.location.origin);
        if (url.origin !== window.location.origin || url.protocol !== 'https:' || url.username || url.password || url.hash || url.pathname !== `/discount/${encodeURIComponent(reward.code)}` || url.searchParams.get('redirect') !== '/cart' || [...url.searchParams.keys()].length !== 1) throw new Error();
        return url.href;
      } catch {
        throw Object.assign(new Error('Invalid product reward destination'), { code: 'INVALID_STATE' });
      }
    }

    static giftCartPriceMatches(line, quote) {
      // Ajax Cart prices use the cart currency. original_price remains the
      // undiscounted unit value if a reward already affects this cart.
      const price = line?.original_price ?? line?.price;
      return Number.isSafeInteger(quote) && quote > 0 && Number.isSafeInteger(price) && price === quote;
    }

    static validateState(state, expectedVersion) {
      const invalid = () => { throw Object.assign(new Error('Invalid rewards response'), { code: 'INVALID_STATE' }); };
      if (!state || state.status !== 'ready' || state.authenticated !== true || state.currency !== 'USD') invalid();
      for (const name of ['availableCents', 'pendingCents', 'reservedCents']) {
        if (!Number.isSafeInteger(state[name]) || Math.abs(state[name]) > 1000000000) invalid();
      }
      if (state.pendingCents < 0 || state.reservedCents < 0) invalid();
      if (typeof state.csrfToken !== 'string' || state.csrfToken.length < 16 || state.csrfToken.length > 2048) invalid();
      if (!state.rules || Object.entries(EXPECTED_RULES).some(([key, value]) => state.rules[key] !== value)) {
        throw Object.assign(new Error('Rules differ from published terms'), { code: 'RULES_CHANGED' });
      }
      if (state.rules.version !== expectedVersion) {
        throw Object.assign(new Error('Rules version differs'), { code: 'RULES_CHANGED' });
      }
      const costs = state.rules.redemptionOptionsCents;
      if (!Array.isArray(costs) || costs.length !== REWARD_COSTS.length || costs.some((cost, index) => cost !== REWARD_COSTS[index])) {
        throw Object.assign(new Error('Reward choices differ'), { code: 'RULES_CHANGED' });
      }
      for (const key of ['reviewAwardCents', 'reviewAnnualLimit', 'contentAwardCents', 'contentAnnualLimit']) {
        const expected = { reviewAwardCents: 200, reviewAnnualLimit: 4, contentAwardCents: 300, contentAnnualLimit: 2 }[key];
        if (state.rules[key] !== expected) throw Object.assign(new Error('Contribution rules differ'), { code: 'RULES_CHANGED' });
      }
      if (!state.capabilities || ['redeem', 'reviewSubmissions', 'contentSubmissions'].some((key) => typeof state.capabilities[key] !== 'boolean')) invalid();
      // Existing financial-v3 accounts have no product catalog until the
      // backend rollout. Absence grants no gift capability.
      if (!Object.prototype.hasOwnProperty.call(state, 'giftOffers')) state.giftOffers = [];
      if (!Array.isArray(state.giftOffers) || state.giftOffers.length > 1) invalid();
      for (const offer of state.giftOffers) {
        if (!MemberRewards.validGiftDefinition(offer) || typeof offer.available !== 'boolean' || typeof offer.canRedeem !== 'boolean' || (offer.canRedeem && (!offer.available || state.availableCents < offer.pointsCost || state.activeReward))) invalid();
        if (Object.prototype.hasOwnProperty.call(offer, 'availability') && (!GIFT_AVAILABILITY.includes(offer.availability) || (offer.availability === 'available') !== offer.available || (offer.availability !== 'available' && offer.canRedeem))) invalid();
        if ((offer.available || offer.productValueCents !== undefined && offer.productValueCents !== null) && (!Number.isSafeInteger(offer.productValueCents) || offer.productValueCents <= 0)) invalid();
      }
      if (!Array.isArray(state.pendingAwards) || !Array.isArray(state.history) || state.pendingAwards.length > 100 || state.history.length > 100) invalid();
      const validDate = (value) => typeof value === 'string' && value.length <= 40 && Number.isFinite(Date.parse(value));
      const validText = (value) => typeof value === 'string' && value.length <= 500;
      if (!Array.isArray(state.orders) || state.orders.length > 50 || state.orders.some((order) => !order || !/^gid:\/\/shopify\/Order\/[1-9][0-9]*$/.test(order.id) || !validText(order.label))) invalid();
      for (const row of state.pendingAwards) {
        if (!row || !validText(row.label) || !Number.isSafeInteger(row.amountCents) || row.amountCents < 0 || typeof row.held !== 'boolean' || (row.releaseAt !== null && !validDate(row.releaseAt))) invalid();
      }
      for (const row of state.history) {
        if (!row || !validText(row.label) || !validDate(row.createdAt) || ['availableDeltaCents', 'pendingDeltaCents', 'reservedDeltaCents'].some((key) => !Number.isSafeInteger(row[key]))) invalid();
      }
      if (state.activeReward !== null) {
        const reward = state.activeReward;
        if (!reward || !['creating', 'active', 'reconciling', 'canceling', 'attention'].includes(reward.status) || !Number.isSafeInteger(reward.amountCents) || (reward.expiresAt !== null && !validDate(reward.expiresAt))) invalid();
        if (reward.kind === 'product') {
          if (!MemberRewards.validGiftDefinition(reward, true) || typeof reward.id !== 'string' || !reward.id || reward.id.length > 200 || reward.amountCents !== reward.pointsCost || (reward.productValueCents !== undefined && reward.productValueCents !== null && (!Number.isSafeInteger(reward.productValueCents) || reward.productValueCents <= 0))) invalid();
        } else if ((reward.kind !== undefined && reward.kind !== 'dollar') || reward.amountCents < 500 || reward.amountCents > 2500 || reward.minimumPurchaseCents !== reward.amountCents * state.rules.minimumPurchaseMultiplier) invalid();
        if (reward.status === 'active' && (typeof reward.code !== 'string' || !CODE_PATTERN.test(reward.code) || !validDate(reward.expiresAt))) invalid();
        if (reward.status === 'active' && reward.kind === 'product') {
          if (!Number.isSafeInteger(reward.productValueCents) || reward.productValueCents <= 0) invalid();
          MemberRewards.productApplyUrl(reward);
        }
      }
      return state;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.state = null;
      this.generation = (this.generation || 0) + 1;
      this.busy = false;
      this.query = (selector) => this.querySelector(selector);
      if ((this.dataset.enabled !== 'true' && !this.isReadOnlyPreview()) || this.dataset.authenticated !== 'true') return;
      if (this.dataset.apiRoot !== '/apps/swe-rewards' || !this.dataset.rulesVersion) return;
      this.listeners = [];
      const listen = (selector, event, handler) => {
        const element = this.query(selector);
        if (!element) return;
        element.addEventListener(event, handler);
        this.listeners.push({ element, event, handler });
      };
      listen('[data-retry]', 'click', () => this.load());
      listen('[data-refresh]', 'click', () => this.load());
      listen('[data-preview-refresh]', 'click', () => this.load());
      if (!this.isReadOnlyPreview()) {
        listen('[data-redeem-form]', 'submit', (event) => this.redeem(event));
        listen('[data-redeem-form] [name="amount"]', 'input', () => this.updateMinimum());
        listen('[data-redeem-form] [name="amount"]', 'change', () => this.updateMinimum());
        listen('[data-copy]', 'click', () => this.copyCode());
        for (const event of ['click', 'auxclick', 'contextmenu']) listen('[data-apply]', event, (interaction) => this.applyReward(interaction));
        listen('[data-submission-form]', 'submit', (event) => this.submitContent(event));
        listen('[data-submission-form] [name="kind"]', 'change', () => this.updateSubmissionKind());
      }
      this.onPageShow = () => this.load();
      this.onVisibility = () => { if (document.visibilityState === 'visible') this.load(); };
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.load();
    }

    disconnectedCallback() {
      this.generation += 1;
      this.clearRewardExpiry();
      this.controller?.abort();
      this.cartController?.abort();
      this.listeners?.forEach(({ element, event, handler }) => element.removeEventListener(event, handler));
      this.listeners = [];
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.initialized = false;
      this.state = null;
    }

    format(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    }

    formatPoints(points) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { maximumFractionDigits: 0 }).format(points);
    }

    pointLabel(points) {
      return `${this.formatPoints(points)} ${Math.abs(points) === 1 ? 'point' : 'points'}`;
    }

    date(value) {
      if (!value || !Number.isFinite(Date.parse(value))) return '';
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }).format(new Date(value));
    }

    setStatus(message, tone = '') {
      const element = this.query('[data-status]');
      if (!element) return;
      element.textContent = message;
      element.dataset.tone = tone;
      element.hidden = !message;
    }

    disableForms() {
      this.querySelectorAll('[data-redeem-form] input, [data-redeem-form] select, [data-redeem-form] button, [data-submission-form] input, [data-submission-form] select, [data-submission-form] textarea, [data-submission-form] button').forEach((element) => { element.disabled = true; });
    }

    fail(error) {
      this.state = null;
      this.renderBonusAvailability(null);
      this.clearRewardExpiry();
      this.disableForms();
      this.query('[data-dashboard]').hidden = true;
      this.query('[data-recovery]').hidden = false;
      this.setStatus(messages[error.code] || messages.NETWORK_ERROR, 'error');
    }

    async request(path, payload) {
      const preview = this.isReadOnlyPreview();
      if ((this.dataset.enabled !== 'true' && !preview) || this.dataset.authenticated !== 'true' || this.dataset.apiRoot !== '/apps/swe-rewards') throw Object.assign(new Error('Disabled'), { code: 'NOT_CONFIGURED' });
      if (preview && (path !== 'state' || payload !== undefined)) throw Object.assign(new Error('Read-only preview'), { code: 'READ_ONLY_PREVIEW' });
      const controller = new AbortController();
      this.controller = controller;
      const timer = window.setTimeout(() => controller.abort(), 12000);
      try {
        const headers = { Accept: 'application/json' };
        const options = { method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', headers, signal: controller.signal };
        if (payload) {
          if (!this.state?.csrfToken) throw Object.assign(new Error('No secure session'), { code: 'INVALID_CSRF' });
          headers['Content-Type'] = 'application/json';
          headers['X-SWE-CSRF'] = this.state.csrfToken;
          options.body = JSON.stringify({ ...payload, csrfToken: this.state.csrfToken });
        }
        const response = await fetch(`${this.dataset.apiRoot}/${path}`, options);
        if (!(response.headers.get('content-type') || '').includes('application/json')) throw Object.assign(new Error('Unexpected response'), { code: 'NETWORK_ERROR' });
        const body = await response.json();
        if (!response.ok) throw Object.assign(new Error('Rewards request failed'), { code: body?.error?.code || 'NETWORK_ERROR' });
        return MemberRewards.validateState(body, this.dataset.rulesVersion);
      } catch (error) {
        if (!error.code) error.code = 'NETWORK_ERROR';
        throw error;
      } finally {
        window.clearTimeout(timer);
        if (this.controller === controller) this.controller = null;
      }
    }

    async load() {
      if (this.busy) return;
      const generation = ++this.generation;
      this.controller?.abort();
      this.state = null;
      this.clearRewardExpiry();
      this.disableForms();
      this.query('[data-dashboard]').hidden = true;
      this.query('[data-recovery]').hidden = true;
      this.setStatus('Loading your rewards securely...');
      this.setAttribute('aria-busy', 'true');
      try {
        const state = await this.request('state');
        if (generation === this.generation && this.isConnected) this.render(state);
      } catch (error) {
        if (generation === this.generation && this.isConnected) this.fail(error);
      } finally {
        if (generation === this.generation) this.removeAttribute('aria-busy');
      }
    }

    render(state) {
      this.state = state;
      this.renderGiftAvailability(state);
      this.renderBonusAvailability(state);
      this.clearRewardExpiry();
      if (state.activeReward) this.redemptionAttempt = null;
      this.query('[data-dashboard]').hidden = false;
      this.query('[data-recovery]').hidden = true;
      this.query('[data-available]').textContent = this.formatPoints(state.availableCents);
      this.query('[data-pending]').textContent = this.pointLabel(state.pendingCents);
      this.query('[data-reserved]').textContent = this.pointLabel(state.reservedCents);
      this.query('[data-balance-equivalent]').textContent = `${this.format(state.availableCents)} toward dollar rewards`;
      this.query('[data-pending-equivalent]').textContent = `${this.format(state.pendingCents)} dollar discount value`;
      this.query('[data-reserved-equivalent]').textContent = state.activeReward?.kind === 'product' ? 'For one 10-inch boot-hooks set' : `${this.format(state.reservedCents)} dollar discount value`;
      const preview = this.isReadOnlyPreview();
      const previewNote = this.query('[data-preview-account-note]');
      if (previewNote) previewNote.hidden = !preview;
      if (preview) {
        this.disableForms();
        this.query('[data-balance-note]').textContent = 'These are your recorded points. Rewards are inactive and points cannot be spent in this preview.';
        this.query('[data-redemption-form-wrap]').hidden = true;
        this.query('[data-active-reward]').hidden = true;
        this.query('[data-code-wrap]').hidden = true;
        this.query('[data-active-code]').textContent = '';
        this.query('[data-apply]').hidden = true;
        this.query('[data-apply]').removeAttribute('href');
        this.query('[data-gift-product-link]').hidden = true;
        this.query('[data-gift-product-link]').removeAttribute('href');
        this.query('[data-submission-section]').hidden = true;
        this.renderPending(state.pendingAwards);
        this.renderHistory(state.history);
        this.setStatus(messages.READ_ONLY_PREVIEW);
        return;
      }
      let note = `Your earned points do not expire. 100 points = $1 toward dollar rewards${this.productRewardEnabled() ? ', or choose a product reward' : ''}.`;
      if (state.availableCents < 0) note = 'A return adjusted your points. Future earnings cover the adjustment. New redemptions are paused.';
      else if (state.availableCents < 500) note = `Earn ${this.pointLabel(500 - state.availableCents)} more to reach your first 500-point reward.`;
      this.query('[data-balance-note]').textContent = note;
      this.query('[data-active-reward]').hidden = !state.activeReward;
      this.query('[data-redemption-form-wrap]').hidden = Boolean(state.activeReward);
      const canRedeem = this.canChooseReward(state);
      this.renderRewardChoices(state);
      if (!canRedeem && !state.activeReward && state.availableCents >= 500) this.setStatus('Redemptions are temporarily unavailable. Your recorded balance is kept.');
      else this.setStatus('');
      if (state.activeReward) this.renderActive(state.activeReward);
      this.renderPending(state.pendingAwards);
      this.renderHistory(state.history);
      this.renderSubmissionOptions(state);
      this.updateMinimum();
    }

    rewardUsable(reward) {
      return reward?.status === 'active' && typeof reward.expiresAt === 'string' && Date.parse(reward.expiresAt) > Date.now();
    }

    clearRewardExpiry() {
      if (this.expiryTimer !== undefined) window.clearTimeout(this.expiryTimer);
      this.expiryTimer = undefined;
    }

    scheduleRewardExpiry(reward) {
      if (!this.rewardUsable(reward)) return;
      // Browsers cap a single timer at about 24.8 days. Longer code lifetimes
      // use another local timer chunk, without making a network request.
      this.expiryTimer = window.setTimeout(() => {
        this.expiryTimer = undefined;
        if (this.isConnected && this.state?.activeReward === reward && !this.isReadOnlyPreview()) this.renderActive(reward);
      }, Math.min(Date.parse(reward.expiresAt) - Date.now(), 2147483647));
    }

    renderActive(reward) {
      this.clearRewardExpiry();
      if (this.isReadOnlyPreview()) {
        this.query('[data-active-reward]').hidden = true;
        this.query('[data-code-wrap]').hidden = true;
        this.query('[data-active-code]').textContent = '';
        this.query('[data-apply]').hidden = true;
        this.query('[data-apply]').removeAttribute('href');
        this.query('[data-gift-product-link]').hidden = true;
        this.query('[data-gift-product-link]').removeAttribute('href');
        return;
      }
      const active = this.rewardUsable(reward);
      const expired = reward.status === 'active' && !active;
      const attention = reward.status === 'attention';
      const product = reward.kind === 'product';
      this.query('[data-active-heading]').textContent = expired ? 'Your reward code has expired.' : attention ? 'Your reward needs a check from our team.' : active ? product ? 'Your free 10-inch boot-hooks set is ready.' : `${this.format(reward.amountCents)} off your next order.` : `${this.pointLabel(reward.amountCents)} reserved while we check your code.`;
      this.query('[data-active-description]').textContent = expired ? `${this.pointLabel(reward.amountCents)} remain reserved until we verify whether the code was used. Unused points return only after that check.` : attention ? `${this.pointLabel(reward.amountCents)} are still reserved. Please contact us for help before creating another reward.` : active ? product ? '1,000 points exchanged for one free 10-inch boot-hooks set. Normal shipping applies. You can redeem it alone or with another order.' : `${this.pointLabel(reward.amountCents)} exchanged for ${this.format(reward.amountCents)} off. Use on ${this.format(reward.minimumPurchaseCents)} or more in eligible merchandise.` : `${this.pointLabel(reward.amountCents)} are reserved. Please wait while we confirm the code. Refresh to check again.`;
      this.query('[data-code-wrap]').hidden = !active;
      this.query('[data-active-code]').textContent = active ? reward.code : '';
      const apply = this.query('[data-apply]');
      const productLink = this.query('[data-gift-product-link]');
      productLink.hidden = !(active && product);
      productLink.removeAttribute('href');
      if (active && product) productLink.setAttribute('href', BOOT_HOOKS_PRODUCT_PATH);
      apply.hidden = !active;
      apply.removeAttribute('href');
      if (active) {
        const cart = /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?cart$/.test(this.dataset.cartUrl) ? this.dataset.cartUrl : '/cart';
        apply.setAttribute('href', product ? MemberRewards.productApplyUrl(reward) : `/discount/${encodeURIComponent(reward.code)}?redirect=${encodeURIComponent(cart)}`);
      }
      this.query('[data-active-expiry]').textContent = active ? `Use the code by ${this.date(reward.expiresAt)}. If it expires unused, the reserved points return after verification.` : 'The points stay reserved until we can confirm the result.';
      if (expired) this.setStatus(messages.EXPIRED_REWARD);
      this.scheduleRewardExpiry(reward);
    }

    async nativeCart(path, payload) {
      if (!['/cart.js', '/cart/add.js'].includes(path)) throw new Error('Unsupported cart request');
      const controller = new AbortController();
      this.cartController = controller;
      const timer = window.setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(path, { method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', headers: payload ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' }, body: payload ? JSON.stringify(payload) : undefined, signal: controller.signal });
        const mediaType = (response.headers.get('content-type') || '').split(';', 1)[0].trim().toLowerCase();
        const responseUrl = new URL(response.url);
        if (!response.ok || response.redirected || responseUrl.origin !== window.location.origin || responseUrl.pathname !== path || responseUrl.search || responseUrl.hash || !['application/json', 'application/javascript', 'text/javascript'].includes(mediaType)) throw new Error('Cart request not confirmed');
        return await response.json();
      } finally {
        window.clearTimeout(timer);
        if (this.cartController === controller) this.cartController = null;
      }
    }

    async applyReward(event) {
      const reward = this.state?.activeReward;
      if (this.isReadOnlyPreview() || !this.rewardUsable(reward)) {
        event.preventDefault();
        if (reward) this.renderActive(reward);
        return;
      }
      if (reward.kind !== 'product') return;
      event.preventDefault();
      if (this.busy || (event.type && event.type !== 'click')) return;
      const generation = this.generation;
      const current = () => this.isConnected && generation === this.generation && this.state?.activeReward === reward && this.rewardUsable(reward);
      this.busy = true;
      this.disableForms();
      const apply = this.query('[data-apply]');
      apply.setAttribute('aria-disabled', 'true');
      apply.removeAttribute('href');
      this.setStatus('Checking your reward and cart for the 10-inch boot hooks...');
      let updatedState;
      let verifiedAccount = false;
      try {
        if (!MemberRewards.validGiftDefinition(reward, true)) throw new Error('Invalid product reward');
        const destination = MemberRewards.productApplyUrl(reward);
        const fresh = await this.request('state');
        if (!current()) throw new Error('Reward changed');
        const verified = fresh.activeReward;
        if (!verified || !this.rewardUsable(verified) || !MemberRewards.validGiftDefinition(verified, true) || ['id', 'code', 'variantId', 'pointsCost', 'amountCents', 'productValueCents', 'expiresAt'].some((key) => verified[key] !== reward[key])) {
          updatedState = fresh;
          throw Object.assign(new Error('Reward changed'), { code: 'REWARD_CHANGED_DURING_APPLY' });
        }
        if (!Number.isSafeInteger(reward.productValueCents) || reward.productValueCents <= 0) throw Object.assign(new Error('Unknown product price'), { code: 'PRODUCT_PRICE_CHANGED' });
        MemberRewards.productApplyUrl(verified);
        verifiedAccount = true;
        const variant = BOOT_HOOKS.variantId.split('/').pop();
        const checkedLines = (cart) => {
          if (cart?.currency !== 'USD') throw Object.assign(new Error('Unsupported cart currency'), { code: 'CART_CURRENCY_UNSUPPORTED' });
          if (!Array.isArray(cart.items) || cart.items.some((line) => !line || !/^[1-9][0-9]*$/.test(String(line.variant_id)) || !Number.isSafeInteger(line.quantity) || line.quantity < 0)) throw new Error('Unverified cart');
          const lines = cart.items.filter((line) => String(line.variant_id) === variant && line.quantity >= 1);
          if (lines.some((line) => !MemberRewards.giftCartPriceMatches(line, reward.productValueCents))) throw Object.assign(new Error('Product price changed'), { code: 'PRODUCT_PRICE_CHANGED' });
          return lines;
        };
        const existing = checkedLines(await this.nativeCart('/cart.js'));
        if (!current()) throw new Error('Reward changed');
        if (!existing.length) {
          const added = await this.nativeCart('/cart/add.js', { items: [{ id: variant, quantity: 1 }] });
          if (!Array.isArray(added?.items) || added.items.length !== 1 || String(added.items[0]?.variant_id) !== variant || !Number.isSafeInteger(added.items[0]?.quantity) || added.items[0].quantity < 1) throw new Error('Cart addition unconfirmed');
          if (!MemberRewards.giftCartPriceMatches(added.items[0], reward.productValueCents)) throw Object.assign(new Error('Product price changed'), { code: 'PRODUCT_PRICE_CHANGED' });
          if (!current()) throw new Error('Reward changed');
          if (!checkedLines(await this.nativeCart('/cart.js')).length) throw new Error('Added set missing from cart');
        }
        if (!current()) throw new Error('Reward changed');
        window.location.assign(destination);
      } catch (error) {
        if (this.isConnected && generation === this.generation && this.state?.activeReward === reward) {
          if (updatedState) this.render(updatedState);
          else if (!verifiedAccount && this.rewardUsable(reward)) this.fail(error);
          else this.renderActive(reward);
          this.setStatus(this.rewardUsable(reward) ? messages[error.code] || messages.CART_NOT_CONFIRMED : messages.CODE_EXPIRED_DURING_APPLY, 'error');
        }
      } finally {
        if (generation === this.generation) {
          this.busy = false;
          apply.removeAttribute('aria-disabled');
          if (this.state && !this.isReadOnlyPreview()) {
            this.renderRewardChoices(this.state);
            this.updateMinimum();
          }
        }
      }
    }

    row(label, amount, detail) {
      const item = document.createElement('li');
      for (const value of [label, amount]) {
        const span = document.createElement('span');
        span.textContent = value;
        item.append(span);
      }
      if (detail) { const small = document.createElement('small'); small.textContent = detail; item.append(small); }
      return item;
    }

    empty(label) {
      const item = document.createElement('li');
      item.className = 'swe-rewards__entry-empty';
      item.textContent = label;
      return item;
    }

    renderPending(awards) {
      const rows = awards.slice(0, 20).map((award) => this.row(award.label, this.pointLabel(award.amountCents), `${this.format(award.amountCents)} in discount value. ${award.held ? 'On hold while a return or dispute is reviewed.' : award.releaseAt ? `Expected ${this.date(award.releaseAt)}.` : 'A release date will appear after shipment.'}`));
      this.query('[data-pending-list]').replaceChildren(...(rows.length ? rows : [this.empty('No pending rewards right now.')]));
    }

    renderHistory(history) {
      const rows = history.slice(0, 30).map((entry) => {
        const deltas = [['available', entry.availableDeltaCents], ['pending', entry.pendingDeltaCents], ['reserved', entry.reservedDeltaCents]].filter(([, value]) => value !== 0);
        const detail = deltas.map(([name, value]) => `${name} ${value > 0 ? '+' : ''}${this.pointLabel(value)}, ${this.format(value)} discount value`).join('; ');
        return this.row(entry.label, this.date(entry.createdAt), detail || 'Balance unchanged.');
      });
      this.query('[data-history-list]').replaceChildren(...(rows.length ? rows : [this.empty('Your rewards activity will appear here.')]));
    }

    renderRewardChoices(state) {
      const select = this.query('[data-redeem-form] [name="amount"]');
      const previous = select.value;
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Choose a points reward';
      const options = state.rules.redemptionOptionsCents.map((cost) => {
        const option = document.createElement('option');
        option.value = String(cost);
        option.textContent = `${this.pointLabel(cost)} = ${this.format(cost)} off`;
        option.disabled = !state.capabilities.redeem || cost > state.availableCents || Boolean(state.activeReward);
        return option;
      });
      for (const gift of this.productRewardEnabled() ? state.giftOffers : []) {
        const option = document.createElement('option');
        option.value = gift.id;
        const availability = gift.availability === 'out_of_stock' ? ', Temporarily out of stock.' : gift.availability === 'not_enabled' ? ', not available yet' : gift.available ? '' : ', availability not confirmed';
        option.textContent = `${this.pointLabel(gift.pointsCost)} = one free ${gift.title} set${availability}`;
        option.disabled = !gift.available || !gift.canRedeem || gift.pointsCost > state.availableCents || Boolean(state.activeReward);
        options.push(option);
      }
      select.replaceChildren(placeholder, ...options);
      select.value = options.some((option) => option.value === previous && !option.disabled) ? previous : '';
      select.disabled = this.busy || !this.canChooseReward(state);
    }

    renderGiftAvailability(state) {
      const card = this.query('[data-gift-card]');
      const status = this.query('[data-gift-stock-status]');
      if (!card || !status) return;
      if (!this.productRewardEnabled()) {
        card.dataset.giftAvailability = 'not_enabled';
        status.textContent = 'Planned reward. Not open yet.';
        status.hidden = false;
        return;
      }
      const gift = state.giftOffers.find((offer) => offer.id === BOOT_HOOKS.id);
      // Older responses grant no stock status. Keep Shopify's initial HTML
      // rather than treating disabled flags or a failed check as stock loss.
      if (!GIFT_AVAILABILITY.includes(gift?.availability)) return;
      card.dataset.giftAvailability = gift.availability;
      status.textContent = gift.availability === 'out_of_stock' ? 'Temporarily out of stock.' : gift.availability === 'unavailable' ? 'Availability not confirmed.' : gift.availability === 'not_enabled' ? 'Planned reward. Not open yet.' : '';
      status.hidden = !status.textContent;
    }

    canChooseReward(state) {
      return !state.activeReward && ((state.capabilities.redeem && state.availableCents >= state.rules.minRedemptionCents) || (this.productRewardEnabled() && state.giftOffers.some((gift) => gift.available && gift.canRedeem && gift.pointsCost <= state.availableCents)));
    }

    selectedReward() {
      if (!this.state || this.state.activeReward) return null;
      const value = this.query('[data-redeem-form] [name="amount"]').value;
      const gift = this.state.giftOffers.find((offer) => offer.id === value);
      if (gift) return this.productRewardEnabled() && gift.available && gift.canRedeem && gift.pointsCost <= this.state.availableCents ? { kind: 'product', pointsCost: gift.pointsCost, body: { rewardId: gift.id } } : null;
      const amountCents = MemberRewards.parseRewardCost(value);
      return this.state.capabilities.redeem && this.state.rules.redemptionOptionsCents.includes(amountCents) && amountCents <= this.state.availableCents ? { kind: 'dollar', pointsCost: amountCents, body: { amountCents } } : null;
    }

    updateMinimum() {
      const selection = this.selectedReward();
      const allowed = !this.isReadOnlyPreview() && !this.busy && selection;
      this.query('[data-redeem-form] button').disabled = !allowed;
      this.query('[data-minimum]').textContent = selection?.kind === 'product' ? '1,000 points gets one free 10-inch boot-hooks set. No additional purchase required. Normal shipping applies. Checkout determines any free-shipping threshold.' : selection ? `${this.pointLabel(selection.pointsCost)} gets ${this.format(selection.pointsCost)} off. Needs ${this.format(selection.pointsCost * this.state.rules.minimumPurchaseMultiplier)} or more in eligible merchandise, before shipping and tax.` : this.productRewardEnabled() ? 'Choose an available dollar reward or the 1,000-point boot-hooks reward. Only dollar rewards require a merchandise minimum.' : 'Choose an available dollar reward to see its minimum eligible merchandise purchase. The boot-hooks reward is not open yet.';
    }

    key() {
      if (!globalThis.crypto?.randomUUID) throw Object.assign(new Error('Secure browser required'), { code: 'NETWORK_ERROR' });
      return globalThis.crypto.randomUUID();
    }

    async redeem(event) {
      event.preventDefault();
      if (this.isReadOnlyPreview()) { this.setStatus(messages.READ_ONLY_PREVIEW); return; }
      if (this.busy || !this.state || this.state.activeReward) return;
      const selection = this.selectedReward();
      if (!selection) {
        this.setStatus('Choose one of the available points rewards, within your points balance.', 'error');
        return;
      }
      this.busy = true;
      const generation = this.generation;
      this.disableForms();
      this.setStatus('Reserving your rewards and preparing your personal code...');
      this.setAttribute('aria-busy', 'true');
      try {
        const signature = JSON.stringify(selection.body);
        if (this.redemptionAttempt?.signature !== signature) this.redemptionAttempt = { signature, idempotencyKey: this.key() };
        const state = await this.request('redeem', { ...selection.body, idempotencyKey: this.redemptionAttempt.idempotencyKey });
        if (!this.isConnected || generation !== this.generation) return;
        this.redemptionAttempt = null;
        this.render(state);
        if (this.rewardUsable(state.activeReward)) this.setStatus('Your personal reward is ready. Apply it below, or copy the code for checkout.');
        else if (state.activeReward?.status === 'active') this.setStatus(messages.EXPIRED_REWARD);
        else if (state.activeReward) this.setStatus('Your reward is reserved. Refresh its status before trying anything else.');
        else this.setStatus('Your rewards are up to date. There is no active reward code waiting.');
      } catch (error) {
        if (this.isConnected && generation === this.generation) this.fail(error);
      } finally {
        if (generation === this.generation) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          if (this.state && !this.isReadOnlyPreview()) {
            this.renderRewardChoices(this.state);
            this.updateMinimum();
          }
        }
      }
    }

    renderSubmissionOptions(state) {
      const { orders } = state;
      const reviewEnabled = this.bonusEnabled('review', state);
      const contentEnabled = this.bonusEnabled('content', state);
      const section = this.query('[data-submission-section]');
      if (this.isReadOnlyPreview()) { section.hidden = true; return; }
      section.hidden = !reviewEnabled && !contentEnabled;
      if (section.hidden) return;
      const select = this.query('[data-submission-form] [name="kind"]');
      const previous = select.value;
      const choices = [];
      if (reviewEnabled) choices.push(['review', 'Honest product review, 200 points after verification']);
      if (contentEnabled) choices.push(['content', 'Original photo or video, 300 points after approval']);
      select.replaceChildren(...choices.map(([value, label]) => { const option = document.createElement('option'); option.value = value; option.textContent = label; return option; }));
      if (choices.some(([value]) => value === previous)) select.value = previous;
      const orderSelect = this.query('[data-submission-form] [name="orderId"]');
      const previousOrder = orderSelect.value;
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = orders.length ? 'Choose an order' : 'No eligible orders available';
      orderSelect.replaceChildren(placeholder, ...orders.map((order) => { const option = document.createElement('option'); option.value = order.id; option.textContent = order.label; return option; }));
      if (orders.some((order) => order.id === previousOrder)) orderSelect.value = previousOrder;
      this.query('[data-order-help]').textContent = orders.length ? 'Your recent eligible website orders are listed. Contact us if the purchase you need is missing.' : 'Eligible SWE website purchases will appear here after they are recorded. Contact us if an order is missing.';
      this.querySelectorAll('[data-submission-form] input, [data-submission-form] select, [data-submission-form] textarea, [data-submission-form] button').forEach((element) => { element.disabled = orders.length === 0; });
      this.updateSubmissionKind();
    }

    updateSubmissionKind() {
      const form = this.query('[data-submission-form]');
      const content = form.elements.kind.value === 'content';
      this.query('[data-consent-wrap]').hidden = !content;
      form.elements.reuseConsent.required = content;
      this.query('[data-review-disclosure]').hidden = content;
    }

    async submitContent(event) {
      event.preventDefault();
      if (this.isReadOnlyPreview()) { this.setStatus(messages.READ_ONLY_PREVIEW); return; }
      if (this.busy || !this.state) return;
      const form = this.query('[data-submission-form]');
      if (!form.reportValidity()) return;
      const kind = form.elements.kind.value;
      if (!this.bonusEnabled(kind)) return;
      const status = this.query('[data-submission-status]');
      let url;
      try { url = new URL(form.elements.url.value.trim()); } catch { status.textContent = 'Enter a valid HTTPS link.'; return; }
      if (url.protocol !== 'https:' || url.username || url.password) { status.textContent = 'Use a shareable HTTPS link without embedded sign-in details.'; return; }
      const payload = { kind, orderId: form.elements.orderId.value.trim(), url: url.href, note: form.elements.note.value.trim(), reuseConsent: kind === 'content' && form.elements.reuseConsent.checked };
      if (!payload.orderId || (kind === 'content' && !payload.reuseConsent)) return;
      this.busy = true;
      const generation = this.generation;
      this.disableForms();
      status.textContent = 'Submitting securely...';
      try {
        const signature = JSON.stringify(payload);
        if (this.submissionAttempt?.signature !== signature) this.submissionAttempt = { signature, idempotencyKey: this.key() };
        const state = await this.request('submissions', { ...payload, idempotencyKey: this.submissionAttempt.idempotencyKey });
        if (!this.isConnected || generation !== this.generation) return;
        this.render(state);
        form.reset();
        this.updateSubmissionKind();
        status.textContent = 'Submission received for review. Any approved reward will appear in your history.';
        this.submissionAttempt = null;
      } catch (error) {
        if (this.isConnected && generation === this.generation) this.fail(error);
      } finally {
        if (generation === this.generation) {
          this.busy = false;
          if (this.state) this.render(this.state);
        }
      }
    }

    async copyCode() {
      if (this.isReadOnlyPreview()) { this.setStatus(messages.READ_ONLY_PREVIEW); return; }
      const reward = this.state?.activeReward;
      if (!this.rewardUsable(reward)) {
        if (reward) this.renderActive(reward);
        return;
      }
      const code = reward.code;
      if (!code || !CODE_PATTERN.test(code)) return;
      try {
        await navigator.clipboard.writeText(code);
        if (this.state?.activeReward === reward) {
          if (this.rewardUsable(reward)) this.setStatus('Reward code copied.');
          else this.renderActive(reward);
        }
      } catch {
        if (this.state?.activeReward === reward) {
          if (this.rewardUsable(reward)) this.setStatus('Select and copy the code above, then paste it at checkout.');
          else this.renderActive(reward);
        }
      }
    }
  }

  customElements.define('swe-member-rewards', MemberRewards);
})();
